import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { setUser } from '../store/authSlice'
import authService from '../services/authService'
import { useTranslation } from '../hooks/use-i18n'

interface LoginForm {
  email: string
  password: string
}

const Login: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    try {
      setIsLoading(true)
      const response = await authService.signIn(data)
      authService.setAuthData(response.user, response.token)
      dispatch(setUser(response.user))
      toast.success(t('toast.welcomeBack'))
      navigate('/dashboard')
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.loginFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-8 pb-12 lg:pt-12 lg:pb-16 px-4 sm:px-6 lg:px-8 bg-[#111111]">
      <div className="max-w-md lg:max-w-lg xl:max-w-xl w-full space-y-8">
        <div className="bg-[#282828] rounded-3xl p-8 lg:p-10 xl:p-12 shadow-2xl">
          <div className="text-center mb-8 lg:mb-10 xl:mb-12">
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 lg:mb-3">
              {t('auth.login.title')}
            </h2>
            <p className="text-white/80 text-base lg:text-lg xl:text-xl">
              {t('auth.login.subtitle')}
            </p>
          </div>
          
          <form className="space-y-6 lg:space-y-8" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4 lg:space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm lg:text-base xl:text-lg font-medium text-white/70 mb-2 lg:mb-3">
                  {t('auth.login.emailLabel')}
                </label>
                <input
                  {...register('email', { 
                    required: t('auth.validation.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('auth.validation.invalidEmail')
                    }
                  })}
                  type="email"
                  className="w-full px-4 lg:px-5 xl:px-6 py-3 lg:py-4 xl:py-5 bg-white border border-white/20 rounded-xl text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm lg:text-base xl:text-lg"
                  placeholder={t('auth.login.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="mt-2 text-sm lg:text-base text-red-400">{errors.email.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm lg:text-base xl:text-lg font-medium text-white/70 mb-2 lg:mb-3">
                  {t('auth.login.passwordLabel')}
                </label>
                <input
                  {...register('password', { required: t('auth.validation.passwordRequired') })}
                  type="password"
                  className="w-full px-4 lg:px-5 xl:px-6 py-3 lg:py-4 xl:py-5 bg-white border border-white/20 rounded-xl text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm lg:text-base xl:text-lg"
                  placeholder={t('auth.login.passwordPlaceholder')}
                />
                {errors.password && (
                  <p className="mt-2 text-sm lg:text-base text-red-400">{errors.password.message}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 lg:py-4 xl:py-5 px-4 lg:px-6 rounded-xl font-medium text-base lg:text-lg xl:text-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-[#282828] text-white/60">{t('auth.login.orDivider')}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => authService.signInWithGoogle()}
              className="w-full flex items-center justify-center gap-2 py-3 lg:py-4 px-4 rounded-xl bg-[#282828] border border-white/20 text-white hover:bg-[#333333] transition-all duration-300 font-medium text-base lg:text-lg"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.18H12v4.13h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t('auth.login.google')}
            </button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm lg:text-base xl:text-lg text-white/60 hover:text-white/80 transition-colors hover:underline"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
          </form>
          
          <div className="mt-6 lg:mt-8 text-center">
            <p className="text-white/60 text-sm lg:text-base xl:text-lg">
              {t('auth.login.noAccount')}{' '}
              <Link
                to="/signup"
                className="font-medium text-blue-500 hover:text-blue-400 transition-colors hover:underline"
              >
                {t('auth.login.signUpLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
